import { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Badge, type BadgeVariant } from '@/components/data-display/Badge';
import { Button } from '@/components/common/Button';
import { RequirePermission } from '@/components/auth';
import { usePermission } from '@/hooks/usePermission';
import { useClientDetail } from './useClientDetail';
import { ClientTabs } from './ClientTabs';
import { KYCTab } from './tabs/KYCTab';
import { PersonsTab } from './tabs/PersonsTab';
import { BeneficiariesTab } from './tabs/BeneficiariesTab';
import { PricingTab } from './tabs/PricingTab';
import { AccountsTab } from './tabs/AccountsTab';
import { HistoryTab } from './tabs/HistoryTab';
import { AuditLogTab } from './tabs/AuditLogTab';
import { DocumentsTab } from './tabs/DocumentsTab';
import { EditClientModal } from './EditClientModal';
import { CLIENT_DETAIL_TABS, TAB_PERMISSIONS, type ClientDetailTab } from './types';

// ---------------------------------------------------------------------------
// Badge variant mapping — keyed on webapp label strings (tenant-independent)
// ---------------------------------------------------------------------------

const KYC_BADGE_VARIANT: Record<string, BadgeVariant> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  expired: 'error',
};

const RISK_BADGE_VARIANT: Record<string, BadgeVariant> = {
  low: 'success',
  standard: 'success',
  medium: 'warning',
  high: 'error',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ClientDetailPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const { hasAny } = usePermission();

  const visibleTabs = useMemo<readonly ClientDetailTab[]>(
    () => CLIENT_DETAIL_TABS.filter((tab) => hasAny(TAB_PERMISSIONS[tab])),
    [hasAny],
  );

  const tabParam = searchParams.get('tab') as ClientDetailTab | null;
  // Fall back to the first tab the operator can see if the URL points at a
  // hidden tab or no tab is set. Picks `kyc` as the historical default when it
  // is visible, otherwise the first permitted tab.
  const activeTab: ClientDetailTab =
    tabParam && visibleTabs.includes(tabParam)
      ? tabParam
      : visibleTabs.includes('kyc')
        ? 'kyc'
        : (visibleTabs[0] ?? 'kyc');

  const handleTabChange = useCallback(
    (tab: ClientDetailTab) => {
      setSearchParams({ tab }, { replace: true });
    },
    [setSearchParams],
  );

  const hook = useClientDetail(clientId ?? '');
  const [editModalOpen, setEditModalOpen] = useState(false);

  if (!clientId) {
    return <div className="p-8 text-center text-[#c62828]">Invalid client ID</div>;
  }

  if (hook.clientLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" text="Loading client..." />
      </div>
    );
  }

  if (hook.clientError || !hook.client) {
    return (
      <div className="p-8 text-center">
        <p className="text-[#c62828]">{hook.clientError ?? 'Client not found'}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/clients')}>
          Back to Clients
        </Button>
      </div>
    );
  }

  const { client } = hook;

  return (
    <div>
      {/* Back button */}
      <button
        className="mb-4 cursor-pointer border-none bg-transparent text-[14px] font-semibold text-[#6B0F1A] hover:underline"
        onClick={() => navigate('/clients')}
      >
        &larr; Back to Clients
      </button>

      {/* Header — matches legacy gradient header */}
      <div className="mb-6 flex items-start justify-between rounded-2xl bg-gradient-to-br from-[#6B0F1A] to-[#3D0008] px-8 py-7 text-white">
        <div>
          <div className="text-[26px] font-extrabold tracking-[0.5px]">
            {client.display_name || client.company_name}
          </div>
          <div className="mt-1 text-[12px] uppercase tracking-[1.5px] opacity-60">
            {client.country_code}
          </div>
          <div className="mt-2 font-mono text-[13px] opacity-50">{client.client_id}</div>
        </div>
        <div className="flex flex-col items-end gap-1.5 text-right">
          <RequirePermission any={['MANAGE_CLIENTS']}>
            <Button
              variant="outline"
              size="sm"
              className="mb-1 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
              onClick={() => setEditModalOpen(true)}
            >
              Edit
            </Button>
          </RequirePermission>
          <Badge variant={client.is_active ? 'success' : 'error'}>
            {client.is_active ? 'Active' : 'Inactive'}
          </Badge>
          <Badge variant={KYC_BADGE_VARIANT[client.kyc_status_name.toLowerCase()] ?? 'default'}>
            {client.kyc_status_name}
          </Badge>
          <Badge variant={RISK_BADGE_VARIANT[client.risk_level_name.toLowerCase()] ?? 'default'}>
            Risk: {client.risk_level_name}
          </Badge>
        </div>
      </div>

      {/* Tabs */}
      <ClientTabs activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Tab content — each tab body is permission-gated so a deep-link to a
          hidden tab (or a removed grant mid-session) doesn't expose data. */}
      <div role="tabpanel" id={`tabpanel-${activeTab}`} aria-labelledby={`tab-${activeTab}`}>
        {activeTab === 'kyc' && (
          <RequirePermission any={TAB_PERMISSIONS.kyc}>
            <KYCTab
              client={client}
              onUpdateKyc={hook.updateKyc}
              onActivate={hook.activateClient}
              onRefresh={hook.refreshClient}
            />
          </RequirePermission>
        )}
        {activeTab === 'persons' && (
          <RequirePermission any={TAB_PERMISSIONS.persons}>
            <PersonsTab
              clientId={clientId}
              persons={hook.persons}
              loading={hook.personsLoading}
              onLoad={hook.loadPersons}
              onCreate={hook.createPerson}
              onLookup={hook.lookupPerson}
              onUpdate={hook.updatePerson}
              onDelete={hook.deletePerson}
              onGrantPortalAccess={hook.grantPortalAccess}
              onRevokePortalAccess={hook.revokePortalAccess}
              onResendInvitation={hook.resendInvitation}
              onGetRights={hook.getPersonRights}
              onUpdateRights={hook.updatePersonRights}
            />
          </RequirePermission>
        )}
        {activeTab === 'beneficiaries' && (
          <RequirePermission any={TAB_PERMISSIONS.beneficiaries}>
            <BeneficiariesTab
              clientId={clientId}
              beneficiaries={hook.beneficiaries}
              loading={hook.beneficiariesLoading}
              onLoad={hook.loadBeneficiaries}
              onCreate={hook.createBeneficiary}
              onUpdate={hook.updateBeneficiary}
              onDelete={hook.deleteBeneficiary}
            />
          </RequirePermission>
        )}
        {activeTab === 'pricing' && (
          <RequirePermission any={TAB_PERMISSIONS.pricing}>
            <PricingTab
              pricing={hook.pricing}
              loading={hook.pricingLoading}
              onLoad={hook.loadPricing}
              onUpdate={hook.updatePricing}
            />
          </RequirePermission>
        )}
        {activeTab === 'accounts' && (
          <RequirePermission any={TAB_PERMISSIONS.accounts}>
            <AccountsTab
              accounts={hook.accounts}
              loading={hook.accountsLoading}
              onLoad={hook.loadAccounts}
              onCreate={hook.createAccount}
            />
          </RequirePermission>
        )}
        {activeTab === 'documents' && (
          <RequirePermission any={TAB_PERMISSIONS.documents}>
            <DocumentsTab
              documents={hook.documents}
              loading={hook.documentsLoading}
              error={hook.documentsError}
              onLoad={hook.loadDocuments}
            />
          </RequirePermission>
        )}
        {activeTab === 'history' && (
          <RequirePermission any={TAB_PERMISSIONS.history}>
            <HistoryTab clientId={clientId} />
          </RequirePermission>
        )}
        {activeTab === 'audit-log' && (
          <RequirePermission any={TAB_PERMISSIONS['audit-log']}>
            <AuditLogTab
              auditLog={hook.auditLog}
              loading={hook.auditLogLoading}
              onLoad={hook.loadAuditLog}
            />
          </RequirePermission>
        )}
      </div>

      {/* Edit Client Modal */}
      <EditClientModal
        isOpen={editModalOpen}
        client={client}
        onClose={() => setEditModalOpen(false)}
        onSave={hook.updateClient}
        onRefresh={hook.refreshClient}
      />
    </div>
  );
}
